import React from 'react';
import WebhookHelp from 'features/agents/WorkflowEditor/WebhookHelp';
import { FieldRow, OptionalParamsSection } from 'features/agents/WorkflowEditor/fields/FieldRow';
import { FieldContext } from 'features/agents/WorkflowEditor/fields/inputs';
import { DecideTest } from 'features/agents/WorkflowEditor/fields/decide';

// The Input / Output panes live in fields/IOPane; re-exported so the Runs
// page, the tools field and the panel keep importing them from here.
export { InputPane, IOPane } from 'features/agents/WorkflowEditor/fields/IOPane';

/**
 * The schema-driven settings form for one node. The editor's side panel and
 * the read-only step view on the Runs page render the same fields from the
 * same code, so the two cannot drift.
 *
 * `issues` ({ [fieldKey]: message }) and `variables` ([{ label, expr }]) are
 * optional: the first puts a message under the offending field, the second
 * fills "Insert variable".
 */
const NO_ISSUES = {};
const NO_VARIABLES = [];

export function NodeConfigFields({ spec, cfg, updateField, updateFields, theme, workflow, readOnly = false, issues = NO_ISSUES, variables = NO_VARIABLES }) {
  const context = React.useMemo(() => ({ issues, variables, readOnly }), [issues, variables, readOnly]);
  if (!spec) return null;
  const schema = spec.configSchema || [];
  const visible = schema
    .filter((field) => !field.optional)
    // `showIf(cfg)` lets a field appear only when another is set to match.
    .filter((field) => (typeof field.showIf === 'function' ? field.showIf(cfg) : true));
  return (
    <FieldContext.Provider value={context}>
      {readOnly ? (
        <p className="text-[11px] text-[color:var(--text-muted)]">Settings this run used. Read only.</p>
      ) : null}
      {schema.length === 0 ? (
        <p className="text-[12px] text-[color:var(--text-muted)]">This step has nothing to set.</p>
      ) : null}
      {visible.map((field, i) => (
        <FieldRow
          key={`${field.key}_${i}`}
          field={field}
          cfg={cfg}
          value={cfg[field.key]}
          onChange={(v) => updateField(field.key, v)}
          onSiblingChange={(k, v) => updateField(k, v)}
          onBatch={updateFields}
          theme={theme}
          nodeKind={spec.kind}
        />
      ))}
      <OptionalParamsSection
        fields={schema.filter((f) => f.optional)}
        cfg={cfg}
        updateField={updateField}
        updateFields={updateFields}
        theme={theme}
        nodeKind={spec.kind}
      />
      {spec.kind === 'trigger_webhook' && workflow ? <WebhookHelp workflow={workflow} requireHmac={!!cfg.require_hmac} /> : null}
      {spec.kind === 'decide' && !readOnly ? <DecideTest cfg={cfg} /> : null}
    </FieldContext.Provider>
  );
}
